import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import css from './Pagination.module.scss';
import Button from '../Button/Button';
import ChevronLeft from '../Icons/ChevronLeft';
import ChevronRight from '../Icons/ChevronRight';

// Module-level counter so that, if a page renders multiple Pagination
// instances with keyboard navigation enabled (e.g. one above and one
// below a table), only the first one to mount claims the document
// keydown listener. Otherwise each arrow key press would fire
// onPageChange once per instance.
let keyboardNavigationOwners = 0;

const DEFAULT_ITEMS_PER_PAGE_OPTIONS = [10, 30, 50, 100];

const Pagination = ({
  itemsPerPage,
  totalItems,
  currentPage,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = DEFAULT_ITEMS_PER_PAGE_OPTIONS,
  enableKeyboardNavigation = false,
}) => {
  const lastPageNumber = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const [draftPage, setDraftPage] = useState(String(currentPage));
  const showItemsPerPageSelector = typeof onItemsPerPageChange === 'function';
  const itemsPerPageSelectOptions = showItemsPerPageSelector
    ? Array.from(new Set([...itemsPerPageOptions, itemsPerPage])).sort((a, b) => a - b)
    : itemsPerPageOptions;

  useEffect(() => {
    setDraftPage(String(currentPage));
  }, [currentPage]);

  useEffect(() => {
    if (!enableKeyboardNavigation) {
      return undefined;
    }
    const isOwner = keyboardNavigationOwners === 0;
    keyboardNavigationOwners += 1;
    if (!isOwner) {
      return () => {
        keyboardNavigationOwners -= 1;
      };
    }
    const handleKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
        return;
      }
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) {
        return;
      }
      if (e.key === 'ArrowLeft' && currentPage > 1) {
        e.preventDefault();
        onPageChange(currentPage - 1);
      } else if (e.key === 'ArrowRight' && currentPage < lastPageNumber) {
        e.preventDefault();
        onPageChange(currentPage + 1);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      keyboardNavigationOwners -= 1;
    };
  }, [enableKeyboardNavigation, currentPage, lastPageNumber, onPageChange]);

  if (totalItems <= itemsPerPage && !showItemsPerPageSelector) {
    return null;
  }

  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(currentPage * itemsPerPage, totalItems);
  const showPageNavigation = totalItems > itemsPerPage;

  const goToPage = (target) => {
    const clamped = Math.min(lastPageNumber, Math.max(1, target));
    setDraftPage(String(clamped));
    if (clamped !== currentPage) {
      onPageChange(clamped);
    }
  };

  const handlePreviousPage = () => {
    goToPage(currentPage - 1);
  };

  const handleNextPage = () => {
    goToPage(currentPage + 1);
  };

  const handleJumpInputChange = (e) => {
    setDraftPage(e.target.value);
  };

  const handleJumpCommit = () => {
    const parsed = parseInt(draftPage, 10);
    if (Number.isFinite(parsed)) {
      goToPage(parsed);
    } else {
      setDraftPage(String(currentPage));
    }
  };

  const handleJumpKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleJumpCommit();
    }
  };

  const handleItemsPerPageChange = (e) => {
    const next = parseInt(e.target.value, 10);
    if (Number.isFinite(next) && next !== itemsPerPage) {
      onItemsPerPageChange(next);
    }
  };

  return (
    <div className={css.container}>
      <span className={css.range}>
        {rangeStart}–{rangeEnd} of {totalItems}
        {showItemsPerPageSelector && (
          <>
            <span className={css.separator}>·</span>
            <select
              className={css.perPageSelect}
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              aria-label="Items per page"
            >
              {itemsPerPageSelectOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <span className={css.jumpLabel}>per page</span>
          </>
        )}
      </span>
      {showPageNavigation && (
        <div className={css.controls}>
          <Button
            type="button"
            variant="tertiary"
            size="small"
            icon={<ChevronLeft />}
            onClick={handlePreviousPage}
            disabled={currentPage <= 1}
          >
            Previous
          </Button>
          <span className={css.jumpRow}>
            <span className={css.jumpLabel}>Page</span>
            <input
              type="number"
              min={1}
              max={lastPageNumber}
              className={css.jumpInput}
              value={draftPage}
              onChange={handleJumpInputChange}
              onBlur={handleJumpCommit}
              onKeyDown={handleJumpKeyDown}
            />
            <span className={css.jumpLabel}>of {lastPageNumber}</span>
          </span>
          <Button
            type="button"
            variant="tertiary"
            size="small"
            icon={<ChevronRight />}
            iconPosition="right"
            onClick={handleNextPage}
            disabled={currentPage >= lastPageNumber}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

Pagination.propTypes = {
  itemsPerPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onItemsPerPageChange: PropTypes.func,
  itemsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  enableKeyboardNavigation: PropTypes.bool,
};

export default Pagination;
